import { useState } from 'react';
import {
  AddItemsToMonitoringAssortment,
  MonitoringAssortmentContainer,
  MonitoringAssortmentsProps,
  NoMonitoringAssortmentsContainer,
  withMonitoringAssortments,
} from '@retail/monitoring-assortment/containers';
import { NotFoundError, SuspenseWithSentryErrorBoundary } from '@retail/components';
import { useSelectedOrgUnit, useUserBrands } from '@retail/app/stores/selected-context';
import { useNavigate, useParams } from 'react-router';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { useAppTFunction } from '@retail/app/i18n';
import {
  DeleteAssortmentDialog,
  EditAssortmentTitleDialog,
  MonitoringAssortmentToolbar,
} from '@retail/monitoring-assortment/components';
import { MgItemContainer, MgItemDialogContainer } from '@retail/products/containers';
import { useDisclosure } from '@shared/hooks';
import { useMonitoringAssortmentActions } from '@retail/monitoring-assortment/context';
import { useToast } from '@retail/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useExportMonitoringAssortmentToExcel } from '@retail/monitoring-assortment/data-access';

export function MonitoringAssortmentPageBase({
  monitoringAssortments,
  selectedAssortment,
}: MonitoringAssortmentsProps) {
  const t = useAppTFunction();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { selectedBrandLogos } = useUserBrands();
  const { assortmentId, selectedOrgUnitAssortment } = useSelectedOrgUnit();

  const [itemToView, setItemToView] = useState<number | undefined>(undefined);
  const showFilters = useDisclosure(true);

  const { assortmentId: paramAssortmentId } = useParams<{ assortmentId: string }>();

  const { isOpen: isDeleteModalOpen, onToggle: toggleDeleteModal } = useDisclosure();
  const { isOpen: isEditNameModalOpen, onToggle: toggleEditNameModal } = useDisclosure();
  const { isOpen: isAddItemsModalOpen, onToggle: toggleAddItemsModal } = useDisclosure();

  const { isLoading, handleDeleteMgAssortment, handlePatchTitleOrItemsOfMgAssortment } =
    useMonitoringAssortmentActions();

  const onDeleteAssortment = (assortmentId: number) => {
    handleDeleteMgAssortment(assortmentId)
      .then(async () => {
        toast.success(t('assortment.admin.delete.success'));
        await queryClient.invalidateQueries({ queryKey: ['orgUnit-assortments'] });
        await navigate(mgPrisRoutes.marketInsight.getFullLinkPath());
      })
      .catch((err) => {
        console.error(err);
        toast.error(t('assortment.admin.update.error'));
        toggleDeleteModal();
      });
  };

  const onUpdateTitle = (assortmentId: number) => (title: string) => {
    handlePatchTitleOrItemsOfMgAssortment({ assortmentId, title })
      .then(() => {
        toggleEditNameModal();
        toast.success(t('assortment.admin.update.assortmentSucess'));
      })
      .catch((err) => {
        console.error(err);
        toast.error(t('assortment.admin.update.error'));
      });
  };

  const { exportToExcel, isLoading: isLoadingExcel } = useExportMonitoringAssortmentToExcel({
    assortmentId: selectedAssortment?.id ?? 0,
    assortmentName: selectedAssortment?.title ?? 'assortment',
  });

  if (!paramAssortmentId) {
    return <NoMonitoringAssortmentsContainer monitoringAssortments={monitoringAssortments} />;
  }

  return (
    <>
      <MonitoringAssortmentToolbar
        selectedAssortment={selectedAssortment}
        selectedOrgUnitId={selectedOrgUnitAssortment.orgUnitId}
        circularLogo={selectedBrandLogos.circularLogo}
        showFilters={showFilters}
        toggleAddItemsModal={toggleAddItemsModal}
        toggleEditNameModal={toggleEditNameModal}
        toggleDeleteModal={toggleDeleteModal}
        dataExport={{
          loading: isLoadingExcel,
          toExcel: exportToExcel,
        }}
      />

      <SuspenseWithSentryErrorBoundary>
        {selectedAssortment ? (
          <MonitoringAssortmentContainer
            selectedAssortment={selectedAssortment}
            showFilters={showFilters.isOpen}
            setItemToView={setItemToView}
            toggleAddItemsModal={toggleAddItemsModal}
          />
        ) : (
          <NotFoundError
            title={t('assortment.mgAssortment.notFound.title')}
            subtitle={t('assortment.mgAssortment.notFound.subtitle', {
              assortmentId: paramAssortmentId,
            })}
            buttonText={t('assortment.mgAssortment.notFound.goBack')}
            navigateTo={mgPrisRoutes.marketInsight.getFullLinkPath()}
          />
        )}
      </SuspenseWithSentryErrorBoundary>

      {itemToView && (
        <MgItemDialogContainer
          open={!!itemToView}
          itemNumber={itemToView}
          onClose={() => setItemToView(undefined)}
        >
          {({ mgItem }) => (
            <MgItemContainer mgItem={mgItem} hiddenBreadcrumbs showMonitorItem={false} />
          )}
        </MgItemDialogContainer>
      )}

      {selectedAssortment && (
        <>
          {isAddItemsModalOpen && (
            <AddItemsToMonitoringAssortment
              open
              onClose={toggleAddItemsModal}
              selectedAssortmentId={selectedAssortment.id}
              assortmentId={assortmentId}
            />
          )}

          <EditAssortmentTitleDialog
            isOpen={isEditNameModalOpen}
            onClose={toggleEditNameModal}
            existingTitle={selectedAssortment.title}
            handleConfirmTitle={onUpdateTitle(selectedAssortment.id)}
            isLoading={isLoading}
          />
          <DeleteAssortmentDialog
            isOpen={isDeleteModalOpen}
            onClose={toggleDeleteModal}
            handleConfirmDelete={() => onDeleteAssortment(selectedAssortment.id)}
            isLoading={isLoading}
          />
        </>
      )}
    </>
  );
}

export const MonitoringAssortmentPage = withMonitoringAssortments(MonitoringAssortmentPageBase);
